// Guest Messaging — templated WhatsApp / SMS from the folio (deep links, logged).
import React, { useCallback, useEffect, useState } from 'react';
import { Copy, Loader2, MessageCircle, MessageSquare, Phone, Send, X } from 'lucide-react';
import { apiCall } from './_shared';

const LANGS = { en: 'English', me: 'Crnogorski', sq: 'Shqip', de: 'Deutsch', ru: 'Русский' };
const fmt = (iso) => (iso || '').slice(5, 16).replace('T', ' ');

const GuestMessageModal = ({ bookingId, guestName, onClose }) => {
  const [data, setData] = useState(null);
  const [lang, setLang] = useState('en');
  const [lateTime, setLateTime] = useState('14:00');
  const [phone, setPhone] = useState('');
  const [tpl, setTpl] = useState(null);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async (l = lang, lt = lateTime) => {
    try {
      const d = await apiCall(`/api/admin/reception/folios/${bookingId}/messages?lang=${l}&late_time=${encodeURIComponent(lt)}`);
      setData(d);
      setPhone((p) => p || d.phone || '');
      if (!tpl) { setTpl(d.templates[0]?.key); setText(d.templates[0]?.text || ''); }
      else { const t = d.templates.find((x) => x.key === tpl); if (t) setText(t.text); }
    } catch (e) { setMsg(e.message); }
  }, [bookingId, lang, lateTime, tpl]);

  useEffect(() => { load(); }, [lang, lateTime]); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = (t) => { setTpl(t.key); setText(t.text); };

  const savePhone = async () => {
    try { await apiCall(`/api/admin/reception/folios/${bookingId}/contact`, { method: 'PUT', body: JSON.stringify({ phone, lang }) }); setMsg('Phone saved.'); }
    catch (e) { setMsg(e.message); }
  };

  const send = async (channel) => {
    setBusy(true); setMsg('');
    try {
      if (phone && phone !== data?.phone) await savePhone();
      const r = await apiCall(`/api/admin/reception/folios/${bookingId}/messages`, {
        method: 'POST', body: JSON.stringify({ channel, template_key: tpl, text, lang, to: phone }),
      });
      if (channel === 'copy') { await navigator.clipboard?.writeText(text); setMsg('Copied to clipboard.'); }
      else if (r.link) { window.open(r.link, '_blank', 'noopener'); setMsg(channel === 'whatsapp' ? 'WhatsApp opened with the message.' : 'SMS app opened.'); }
      await load();
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  };

  const tplBtns = (data?.templates || []).map((t) => (
    <button key={t.key} onClick={() => pick(t)} className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${tpl === t.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 hover:bg-slate-50'}`} data-testid={`msg-template-${t.key}`}>{t.label}</button>
  ));
  const history = (data?.history || []).map((h) => (
    <li key={h.id} className="py-1.5 text-xs" data-testid={`msg-history-${h.id}`}>
      <span className="font-semibold capitalize text-slate-700">{h.channel}</span> <span className="text-slate-400">· {h.by} · {fmt(h.at)} · {h.to || ''}</span>
      <p className="text-slate-600 truncate">{h.text}</p>
    </li>
  ));

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="guest-message-modal">
        <div className="p-4 bg-emerald-600 text-white flex justify-between items-center">
          <h3 className="font-bold flex items-center gap-2"><MessageCircle className="w-4 h-4" /> Message {guestName}</h3>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg" data-testid="guest-message-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="grid sm:grid-cols-[1fr_140px_110px] gap-2">
            <label className="text-xs text-slate-600">Guest phone (with country code)
              <div className="flex gap-1 mt-1">
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+382 69 …" className="flex-1 border rounded-lg px-3 py-2 text-sm" data-testid="msg-phone" />
                <button onClick={savePhone} className="px-2 border rounded-lg hover:bg-slate-50" title="Save phone" data-testid="msg-phone-save"><Phone className="w-4 h-4 text-slate-600" /></button>
              </div>
            </label>
            <label className="text-xs text-slate-600">Language
              <select value={lang} onChange={(e) => setLang(e.target.value)} className="mt-1 w-full border rounded-lg px-2 py-2 text-sm" data-testid="msg-lang">
                {Object.entries(LANGS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
            <label className="text-xs text-slate-600">Late check-out
              <input type="time" value={lateTime} onChange={(e) => setLateTime(e.target.value)} className="mt-1 w-full border rounded-lg px-2 py-2 text-sm" data-testid="msg-late-time" />
            </label>
          </div>
          <div className="flex flex-wrap gap-1.5" data-testid="msg-templates">{tplBtns}</div>
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={5} className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="msg-text" />
          <div className="flex flex-wrap gap-2 items-center">
            <button onClick={() => send('whatsapp')} disabled={busy || !text.trim()} className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-50" data-testid="msg-send-whatsapp">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} WhatsApp
            </button>
            <button onClick={() => send('sms')} disabled={busy || !text.trim()} className="px-3 py-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold flex items-center gap-1 disabled:opacity-50" data-testid="msg-send-sms">
              <MessageSquare className="w-4 h-4" /> SMS
            </button>
            <button onClick={() => send('copy')} disabled={busy || !text.trim()} className="px-3 py-2 rounded-lg border text-sm font-semibold flex items-center gap-1 hover:bg-slate-50 disabled:opacity-50" data-testid="msg-copy">
              <Copy className="w-4 h-4" /> Copy
            </button>
            {msg && <span className="text-xs text-slate-600" data-testid="msg-status">{msg}</span>}
          </div>
          {data?.invoice_url && <p className="text-[11px] text-slate-400 break-all">Guest invoice link: {data.invoice_url}</p>}
          {history.length > 0 && (
            <div className="border-t pt-2">
              <p className="text-xs font-semibold text-slate-700 mb-1">Sent messages</p>
              <ul className="divide-y max-h-40 overflow-y-auto" data-testid="msg-history">{history}</ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GuestMessageModal;
